import { supabase } from "@/integrations/supabase/client";
import type { Notification } from "@/lib/db/models";
import {
  categoryForKind,
  notificationCategory,
  type NotificationCategory,
} from "@/lib/notifications/model";

export type { NotificationCategory };
export { categoryForKind, notificationCategory };

/** @deprecated use categoryForKind — kept for older call sites. */
export function categorizeNotification(kind: string): NotificationCategory {
  return categoryForKind(kind);
}

const SELECT_COLUMNS =
  "id,user_id,kind,category,title,body,status,read_at,dismissed_at,archived_at,created_at,review_id,negotiation_id,report_id,billing_record_id";

export const notificationService = {
  /**
   * Every notification the user owns, including dismissed ones. The UI filters
   * dismissed rows out; keeping them in the payload allows "show dismissed"
   * and undo without a refetch.
   */
  async listMine(userId: string): Promise<Notification[]> {
    const { data, error } = await supabase
      .from("notifications")
      .select(SELECT_COLUMNS)
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw error;
    return (data ?? []) as unknown as Notification[];
  },

  async getMyNotifications(userId: string): Promise<Notification[]> {
    return this.listMine(userId);
  },

  /** Canonical badge count: unread and not dismissed. */
  async getUnreadCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .is("read_at", null)
      .is("dismissed_at", null);
    if (error) throw error;
    return count ?? 0;
  },

  async markRead(id: string) {
    const { error } = await supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("id", id)
      .is("read_at", null);
    if (error) throw error;
  },

  async markAsRead(id: string) {
    return this.markRead(id);
  },

  async markAllAsRead(userId: string) {
    const { error } = await supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", userId)
      .is("read_at", null);
    if (error) throw error;
  },

  /** Hide without deleting. */
  async dismiss(id: string) {
    const { error } = await supabase
      .from("notifications")
      .update({ dismissed_at: new Date().toISOString() })
      .eq("id", id);
    if (error) throw error;
  },

  async undismiss(id: string) {
    const { error } = await supabase
      .from("notifications")
      .update({ dismissed_at: null })
      .eq("id", id);
    if (error) throw error;
  },

  async archive(id: string) {
    const { error } = await supabase
      .from("notifications")
      .update({ archived_at: new Date().toISOString() })
      .eq("id", id);
    if (error) throw error;
  },

  async createNotification(input: {
    user_id: string;
    kind: string;
    title: string;
    body?: string | null;
    review_id?: string | null;
    negotiation_id?: string | null;
    report_id?: string | null;
    billing_record_id?: string | null;
  }): Promise<Notification | null> {
    const { data, error } = await supabase
      .from("notifications")
      .insert({
        user_id: input.user_id,
        kind: input.kind,
        category: categoryForKind(input.kind),
        title: input.title,
        body: input.body ?? null,
        review_id: input.review_id ?? null,
        negotiation_id: input.negotiation_id ?? null,
        report_id: input.report_id ?? null,
        billing_record_id: input.billing_record_id ?? null,
      })
      .select(SELECT_COLUMNS)
      .maybeSingle();
    if (error) throw error;
    return (data as unknown as Notification | null) ?? null;
  },
};
